// src/lib/query/persistenceRules.ts
/**
 * @fileoverview Defines which query keys should be persisted
 *
 * Security-critical: NEVER persist tokens, credentials, or sensitive data
 */

import { Query } from '@tanstack/react-query';

/**
 * Query key prefixes that should be PERSISTED
 */
export const PERSISTABLE_PREFIXES = [
  'users',
  'theme',
  'cms',
  'dashboard',
  'images',
  'forms',
  'formGroups',
] as const;

/**
 * Query key prefixes that should NEVER be persisted
 */
export const NON_PERSISTABLE_PREFIXES = [
  'notifications',
  'meetings',
  'advisor',
  'menuConnections',
  'fields',
] as const;

/**
 * Sensitive patterns that must NEVER be stored
 */
const SENSITIVE_PATTERNS = [
  /token/i,
  /auth/i,
  /credential/i,
  /password/i,
  /secret/i,
  /session/i,
] as const;

const containsSensitiveData = (queryKey: readonly unknown[]): boolean => {
  const keyString = JSON.stringify(queryKey).toLowerCase();
  return SENSITIVE_PATTERNS.some(pattern => pattern.test(keyString));
};

const getQueryKeyPrefix = (queryKey: readonly unknown[]): string | null => {
  if (!Array.isArray(queryKey) || queryKey.length === 0) return null;
  return typeof queryKey[0] === 'string' ? queryKey[0] : null;
};

/**
 * Determines if a query should be persisted
 */
export const shouldPersistQuery = (query: Query): boolean => {
  const { queryKey, state, meta } = query;

  // Check meta for explicit persistence flag
  if (meta?.persist === false) return false;

  // Never persist sensitive data
  if (containsSensitiveData(queryKey)) return false;

  // Never persist failed or empty queries
  if (state.status === 'error' || state.data === undefined) return false;

  const prefix = getQueryKeyPrefix(queryKey);
  if (!prefix) return false;

  // Explicit deny list takes precedence
  if ((NON_PERSISTABLE_PREFIXES as readonly string[]).includes(prefix)) return false;

  // Must be in allow list
  return (PERSISTABLE_PREFIXES as readonly string[]).includes(prefix);
};

/**
 * Helper to create non-persistable query options
 */
export const nonPersistable = () => ({ meta: { persist: false } });
